import React, { useState, useEffect } from 'react';
import { useUser } from '../../contexts/UserContext';
import { db } from '../../config/firebase';
import { FIRESTORE_PATHS } from '../../config/appConfig';
import { doc, getDoc, setDoc, updateDoc, increment } from 'firebase/firestore';

import Onboarding from './Onboarding';
import PlanReveal from './PlanReveal';
import DailyCheckIn from './DailyCheckIn';
import Dashboard from './Dashboard';
import MatchLogger from './MatchLogger';
import TrainingLogger from './TrainingLogger';
import SessionRunner from './SessionRunner';
import { generateWeeklyPlan } from '../../utils/planner';
import logger from '../../utils/logger';

const CareerModule = () => {
    const { user } = useUser();
    const [loading, setLoading] = useState(true);
    const [mode, setMode] = useState('loading'); // loading, onboarding, dashboard, match_log, training_log, session
    const [showCheckIn, setShowCheckIn] = useState(false);
    const [playerProfile, setPlayerProfile] = useState(null);
    const [sessionPayload, setSessionPayload] = useState(null); // { day, drills }

    useEffect(() => {
        const checkUserProfile = async () => {
            if (!user) {
                setLoading(false);
                return;
            }

            try {
                // Flattened: Users/{userId}/TrainingProfile/current
                const trainingProfileRef = doc(db, FIRESTORE_PATHS.USERS, user.uid, FIRESTORE_PATHS.TRAINING_PROFILE.BASE, FIRESTORE_PATHS.TRAINING_PROFILE.CURRENT);
                const trainingProfileDoc = await getDoc(trainingProfileRef);

                if (trainingProfileDoc.exists() && trainingProfileDoc.data().role) {
                    let profileData = trainingProfileDoc.data();

                    // Migration: Generate Plan if missing
                    if (!profileData.weeklyPlan) {
                        const newPlan = generateWeeklyPlan('In-Season');
                        await setDoc(trainingProfileRef, { weeklyPlan: newPlan }, { merge: true });
                        profileData.weeklyPlan = newPlan;
                    }

                    setPlayerProfile(profileData);
                    setMode('dashboard');

                    // Check for daily check-in
                    const lastCheckIn = localStorage.getItem(`last_checkin_${user.uid}`);
                    const today = new Date().toDateString();

                    if (lastCheckIn !== today) {
                        setShowCheckIn(true);
                    }
                } else {
                    setMode('onboarding');
                }
            } catch (error) {
                logger.error('CareerModule', 'Error fetching user profile:', error);
            } finally {
                setLoading(false);
            }
        };

        checkUserProfile();
    }, [user]);

    const handleOnboardingComplete = async (data) => {
        if (!user) return;
        try {
            const initialPlan = generateWeeklyPlan('In-Season');
            // Flattened: Users/{userId}/TrainingProfile/current
            await setDoc(doc(db, FIRESTORE_PATHS.USERS, user.uid, FIRESTORE_PATHS.TRAINING_PROFILE.BASE, FIRESTORE_PATHS.TRAINING_PROFILE.CURRENT), {
                ...data,
                grindScore: 50,
                totalXp: 0,
                joinedAt: new Date(),
                weeklyPlan: initialPlan
            }, { merge: true });

            setPlayerProfile({ ...data, weeklyPlan: initialPlan });
            setMode('plan_reveal');
        } catch (error) {
            logger.error('CareerModule', 'Error saving profile:', error);
        }
    };

    const handlePlanRevealComplete = () => {
        setMode('dashboard');
    };

    const handleCheckInComplete = (data) => {
        localStorage.setItem(`last_checkin_${user.uid}`, new Date().toDateString());
        setShowCheckIn(false);

        if (data.played) {
            if (data.type === 'match') setMode('match_log');
            else setMode('training_log');
        }
    };

    const handleLogActivity = (type) => {
        if (type === 'match') setMode('match_log');
        else setMode('training_log');
    };

    const handleStartSession = (day, drills) => {
        setSessionPayload({ day, drills });
        setMode('session');
    };

    const handleSessionComplete = async (results) => {
        // Save results to Firestore - organized structure
        try {
            const trainingProfileRef = doc(db, FIRESTORE_PATHS.USERS, user.uid, FIRESTORE_PATHS.TRAINING_PROFILE.BASE, FIRESTORE_PATHS.TRAINING_PROFILE.CURRENT);
            const trainingStatsRef = doc(db, FIRESTORE_PATHS.USERS, user.uid, FIRESTORE_PATHS.STATS.BASE, FIRESTORE_PATHS.STATS.WEB_TRAINING);

            // Initialize profile if doesn't exist
            const profileDoc = await getDoc(trainingProfileRef);
            if (!profileDoc.exists()) {
                await setDoc(trainingProfileRef, {
                    grindScore: 0,
                    totalXp: 0,
                    role: playerProfile?.role || '',
                    weeklyPlan: playerProfile?.weeklyPlan || []
                });
            }

            await updateDoc(trainingProfileRef, {
                totalXp: increment(150), // Fixed XP for now
                grindScore: increment(2)
            });

            // Initialize stats if doesn't exist, then increment
            const statsDoc = await getDoc(trainingStatsRef);
            if (!statsDoc.exists()) {
                await setDoc(trainingStatsRef, { trainingSessions: 0 });
            }
            await updateDoc(trainingStatsRef, {
                trainingSessions: increment(1)
            });

            await refreshProfile();
        } catch (error) {
            logger.error('CareerModule', 'Error saving session:', error);
        }
        setMode('dashboard');
        setSessionPayload(null);
    };

    const refreshProfile = async () => {
        if (!user) return;
        try {
            // Flattened: Users/{userId}/TrainingProfile/current
            const trainingProfileRef = doc(db, FIRESTORE_PATHS.USERS, user.uid, FIRESTORE_PATHS.TRAINING_PROFILE.BASE, FIRESTORE_PATHS.TRAINING_PROFILE.CURRENT);
            const trainingProfileDoc = await getDoc(trainingProfileRef);
            if (trainingProfileDoc.exists()) {
                setPlayerProfile(trainingProfileDoc.data());
            }
        } catch (err) {
            logger.error('CareerModule', 'Error refreshing profile:', err);
        }
    };

    const handleSaveActivity = async () => {
        await refreshProfile();
        setMode('dashboard');
    };

    if (loading) return <div className="flex items-center justify-center h-full text-white">Loading Career Profile...</div>;

    if (mode === 'onboarding') {
        return <Onboarding onComplete={handleOnboardingComplete} />;
    }

    if (mode === 'plan_reveal') {
        return <PlanReveal profile={playerProfile} onComplete={handlePlanRevealComplete} />;
    }

    if (mode === 'match_log') {
        return <MatchLogger userProfile={playerProfile} onSave={handleSaveActivity} onCancel={() => setMode('dashboard')} />;
    }

    if (mode === 'training_log') {
        return <TrainingLogger onSave={handleSaveActivity} onCancel={() => setMode('dashboard')} />;
    }

    if (mode === 'session' && sessionPayload) {
        return (
            <SessionRunner
                sessionData={sessionPayload.day}
                drills={sessionPayload.drills}
                onComplete={handleSessionComplete}
                onExit={() => setMode('dashboard')}
            />
        );
    }

    return (
        <div className="relative h-full">
            <Dashboard
                user={{ ...user, profile: playerProfile }}
                onLogActivity={handleLogActivity}
                onStartSession={handleStartSession}
            />
            {showCheckIn && <DailyCheckIn onComplete={handleCheckInComplete} />}
        </div>
    );
};

export default CareerModule;
